import React, { useEffect, useState } from 'react';
import { Screen } from '../App';
import { User } from '../storage';
import * as authApi from '../authApi';
import { selectTier, formatPrice } from '../tiers';

interface Props { navigate: (s: Screen) => void; user: User | null; }

const DAY = 24 * 60 * 60 * 1000;

// Days until a license expires (negative = already expired). null if no date.
function daysUntil(expiresAt: string | null): number | null {
  if (!expiresAt) return null;
  return Math.ceil((new Date(expiresAt).getTime() - Date.now()) / DAY);
}
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
// <input type="date"> wants YYYY-MM-DD.
function toDateInput(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}

const LEAD_STATUS_OPTIONS = ['new', 'contacted', 'demo booked', 'quoted', 'accepted', 'won', 'lost'];
const OUTREACH_STATUS_OPTIONS = ['queued', 'sent_1', 'sent_2', 'sent_3', 'replied', 'booked', 'unsubscribed', 'bounced'];
const STUDENT_OPTIONS = ['Up to 300', 'Up to 600', 'Up to 900', 'Up to 1,200', 'More than 1,200'];
function fmtDateTime(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function Admin({ navigate, user }: Props) {
  const [tab, setTab] = useState<'licenses' | 'leads' | 'outreach'>('licenses');
  const [licenses, setLicenses] = useState<authApi.License[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Leads (school license requests)
  const [leads, setLeads] = useState<authApi.Lead[]>([]);
  const [leadsLoaded, setLeadsLoaded] = useState(false);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsErr, setLeadsErr] = useState('');

  // Quote form (per lead)
  const [quoteFor, setQuoteFor] = useState<string | null>(null);
  const [quoteCount, setQuoteCount] = useState('');
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [quoteMsg, setQuoteMsg] = useState('');

  // Outreach contacts
  const [outreach, setOutreach] = useState<authApi.OutreachContact[]>([]);
  const [outreachLoaded, setOutreachLoaded] = useState(false);
  const [outreachLoading, setOutreachLoading] = useState(false);
  const [outreachErr, setOutreachErr] = useState('');
  const [outreachFilter, setOutreachFilter] = useState('');

  // Create form
  const [name, setName] = useState('');
  const [seats, setSeats] = useState('');
  const [expiry, setExpiry] = useState('');

  // Usage metering (Stage 4)
  const [usage, setUsage] = useState<authApi.UsageRow[]>([]);
  const [usagePeriod, setUsagePeriod] = useState('');

  // Email diagnostics — confirm signup notifications actually deliver.
  const [emailTesting, setEmailTesting] = useState(false);
  const [emailResult, setEmailResult] = useState<authApi.TestEmailResult | null>(null);
  const sendTestEmail = async () => {
    setEmailTesting(true); setEmailResult(null);
    try {
      setEmailResult(await authApi.adminSendTestEmail());
    } catch (e: any) {
      setEmailResult({ ok: false, to: '', from: '', hasKey: false, error: e?.message || 'Request failed.' });
    } finally {
      setEmailTesting(false);
    }
  };

  const load = async () => {
    setError('');
    try {
      setLicenses(await authApi.adminListLicenses());
    } catch (e: any) {
      console.error('admin load error', e);
      setError('Could not load licenses. Please try again.');
    } finally {
      setLoading(false);
    }
    try {
      const u = await authApi.adminGetUsage();
      setUsage(u.usage);
      setUsagePeriod(u.period);
    } catch (e: any) {
      console.error('admin usage load error', e);
    }
  };

  useEffect(() => { load(); }, []);

  const loadLeads = async () => {
    setLeadsLoading(true); setLeadsErr('');
    try {
      setLeads(await authApi.adminListLeads());
      setLeadsLoaded(true);
    } catch (e: any) {
      console.error('admin leads load error', e);
      setLeadsErr('Could not load leads. Please try again.');
    } finally {
      setLeadsLoading(false);
    }
  };

  const loadOutreach = async (status?: string) => {
    setOutreachLoading(true); setOutreachErr('');
    try {
      setOutreach(await authApi.adminListOutreach(status || undefined));
      setOutreachLoaded(true);
    } catch (e: any) {
      console.error('admin outreach load error', e);
      setOutreachErr('Could not load outreach contacts. Please try again.');
    } finally {
      setOutreachLoading(false);
    }
  };

  const changeOutreachFilter = (status: string) => {
    setOutreachFilter(status);
    loadOutreach(status);
  };

  // Lazy-load each tab's data the first time it is opened.
  const openTab = (t: 'licenses' | 'leads' | 'outreach') => {
    setTab(t);
    if (t === 'leads' && !leadsLoaded && !leadsLoading) loadLeads();
    if (t === 'outreach' && !outreachLoaded && !outreachLoading) loadOutreach(outreachFilter);
  };

  const openQuote = (l: authApi.Lead) => {
    setQuoteMsg('');
    setQuoteFor(quoteFor === l.id ? null : l.id);
    // Prefill from the lead if it matches one of the ranges, else leave blank.
    setQuoteCount(l.studentCount && STUDENT_OPTIONS.includes(l.studentCount) ? l.studentCount : '');
  };

  const sendQuote = async (id: string) => {
    setQuoteBusy(true); setQuoteMsg('');
    try {
      await authApi.adminSendQuote(id, quoteCount || undefined);
      setQuoteFor(null);
      await loadLeads();
    } catch (e: any) {
      setQuoteMsg(e?.message || 'Could not send the quote. Please try again.');
    } finally {
      setQuoteBusy(false);
    }
  };

  const changeLeadStatus = async (id: string, status: string) => {
    const prev = leads;
    setLeads(prev.map(l => l.id === id ? { ...l, status } : l)); // optimistic
    try {
      await authApi.adminUpdateLeadStatus(id, status);
    } catch (e: any) {
      setLeads(prev); // revert on failure
      setLeadsErr(e?.message || 'Could not update that lead. Please try again.');
    }
  };

  // Build a CSV from the current leads and hand it to the browser to download.
  const downloadCsv = () => {
    const headers = ['Created', 'School', 'District', 'State', 'Students', 'Contact', 'Role', 'Work email', 'Phone', 'Status', 'Situation'];
    const cell = (v: any) => {
      const s = String(v == null ? '' : v);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const rows = leads.map(l => [fmtDateTime(l.createdAt), l.schoolName, l.district, l.state, l.studentCount, l.contactName, l.role, l.workEmail, l.phone, l.status, l.situation]);
    const csv = [headers, ...rows].map(r => r.map(cell).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `decode-my-student-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadOutreachCsv = () => {
    const headers = ['Email', 'First name', 'Last name', 'Title', 'Organization', 'State', 'Segment', 'Source', 'Status', 'Last sent', 'Next send', 'Notes', 'Created'];
    const cell = (v: any) => {
      const s = String(v == null ? '' : v);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const rows = outreach.map(c => [c.email, c.firstName, c.lastName, c.title, c.organization, c.state, c.segment, c.source, c.status, fmtDateTime(c.lastSentAt), fmtDateTime(c.nextSendAt), c.notes, fmtDateTime(c.createdAt)]);
    const csv = [headers, ...rows].map(r => r.map(cell).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `decode-my-student-outreach-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const create = async () => {
    setError('');
    const seatCount = parseInt(seats, 10);
    if (!name.trim()) { setError('Please enter a school name.'); return; }
    if (!Number.isFinite(seatCount) || seatCount < 1) { setError('Seat count must be at least 1.'); return; }
    if (!expiry) { setError('Please choose an expiry date.'); return; }
    setBusy(true);
    try {
      await authApi.adminCreateLicense({ name: name.trim(), seatCount, expiresAt: expiry });
      setName(''); setSeats(''); setExpiry('');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Could not create the license.');
    } finally {
      setBusy(false);
    }
  };

  const extend = async (id: string, newDate: string) => {
    if (!newDate) return;
    setBusy(true); setError('');
    try {
      await authApi.adminUpdateLicense({ licenseId: id, expiresAt: newDate });
      await load();
    } catch (e: any) {
      setError(e?.message || 'Could not update the expiry date.');
    } finally { setBusy(false); }
  };

  const setSeatCount = async (id: string, count: number) => {
    setBusy(true); setError('');
    try {
      await authApi.adminUpdateLicense({ licenseId: id, seatCount: count });
      await load();
    } catch (e: any) {
      setError(e?.message || 'Could not update the seat count.');
    } finally { setBusy(false); }
  };

  const removeTeacher = async (teacherId: string, teacherName: string) => {
    if (!window.confirm(`Remove ${teacherName} from this license? Their account and data stay — they just revert to unpaid and the seat frees up.`)) return;
    setBusy(true); setError('');
    try {
      await authApi.adminRemoveTeacher(teacherId);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Could not remove the teacher.');
    } finally { setBusy(false); }
  };

  const expiringSoon = licenses.filter(l => { const d = daysUntil(l.expiresAt); return d !== null && d >= 0 && d <= 60; });

  return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate('dashboard')} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>
          Dashboard
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>School Licenses</span>
        <div style={{ width: 60 }} />
      </div>

      <div className="page-content">
        {/* Tabs: existing license admin, plus the school-license leads inbox. */}
        <div className="tabs" style={{ marginBottom: 16 }}>
          <button className={`tab${tab === 'licenses' ? ' active' : ''}`} onClick={() => openTab('licenses')}>Licenses</button>
          <button className={`tab${tab === 'leads' ? ' active' : ''}`} onClick={() => openTab('leads')}>Leads</button>
          <button className={`tab${tab === 'outreach' ? ' active' : ''}`} onClick={() => openTab('outreach')}>Outreach</button>
        </div>

        {tab === 'licenses' && (<>
        {error && <div style={{ background: 'var(--coral-light)', color: 'var(--coral)', padding: '12px 16px', borderRadius: 8, fontSize: 14, marginBottom: 16 }}>{error}</div>}

        {/* Expiring-soon summary */}
        {expiringSoon.length > 0 && (
          <div style={{ background: 'var(--amber-light)', border: '1px solid var(--amber)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
            <p style={{ fontWeight: 700, color: '#b87800', marginBottom: 6 }}>⏰ Expiring in the next 60 days</p>
            {expiringSoon.map(l => (
              <p key={l.id} style={{ fontSize: 14, color: 'var(--charcoal)' }}>
                {l.name} — {daysUntil(l.expiresAt)} days ({fmtDate(l.expiresAt)})
              </p>
            ))}
          </div>
        )}

        {/* Create a license */}
        <div className="card" style={{ marginBottom: 20 }}>
          <p style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Create a license</p>
          <div className="form-group"><label>School name</label><input value={name} onChange={e => setName(e.target.value)} placeholder="Lincoln Elementary" /></div>
          <div className="grid-2">
            <div className="form-group"><label>Seats</label><input type="number" min={1} value={seats} onChange={e => setSeats(e.target.value)} placeholder="25" /></div>
            <div className="form-group"><label>Expires</label><input type="date" value={expiry} onChange={e => setExpiry(e.target.value)} /></div>
          </div>
          <button onClick={create} className="btn-primary" disabled={busy}>{busy ? 'Working…' : 'Create license'}</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}><div className="spinner" /></div>
        ) : licenses.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', padding: '24px 0' }}>No licenses yet. Create one above.</p>
        ) : (
          licenses.map(l => {
            const d = daysUntil(l.expiresAt);
            const expired = d !== null && d < 0;
            const soon = d !== null && d >= 0 && d <= 60;
            return (
              <div key={l.id} className="card" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <p style={{ fontWeight: 700, fontSize: 16 }}>{l.name}</p>
                  {expired ? <span className="badge badge-coral">Expired</span> : soon ? <span className="badge badge-amber">Expires in {d}d</span> : <span className="badge badge-green">Active</span>}
                </div>

                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, fontSize: 14 }}>
                  <span><strong>{l.seatsUsed}</strong> / {l.seatCount} seats used</span>
                  <span>Code: <span className="mono" style={{ background: 'var(--teal-light)', color: 'var(--teal)', padding: '2px 8px', borderRadius: 6, fontWeight: 600 }}>{l.joinCode}</span></span>
                  <span>Expires: {fmtDate(l.expiresAt)}</span>
                </div>

                {/* Extend expiry + adjust seats */}
                <div className="grid-2" style={{ marginBottom: 12 }}>
                  <div>
                    <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Extend expiry</label>
                    <input type="date" defaultValue={toDateInput(l.expiresAt)} onChange={e => extend(l.id, e.target.value)} disabled={busy} style={{ width: '100%' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Seat count</label>
                    <input type="number" min={1} defaultValue={l.seatCount} disabled={busy}
                      onBlur={e => { const n = parseInt(e.target.value, 10); if (Number.isFinite(n) && n >= 1 && n !== l.seatCount) setSeatCount(l.id, n); }}
                      style={{ width: '100%' }} />
                  </div>
                </div>

                {/* Members */}
                <p style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Teachers on this license ({l.members.length})</p>
                {l.members.length === 0 ? (
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No one has joined yet.</p>
                ) : (
                  l.members.map(m => (
                    <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: '1px solid #f0ece6' }}>
                      <div>
                        <p style={{ fontSize: 14, fontWeight: 600 }}>{m.name || m.email}</p>
                        <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{m.email}</p>
                      </div>
                      <button onClick={() => removeTeacher(m.id, m.name || m.email)} disabled={busy} style={{ background: 'none', color: 'var(--coral)', fontSize: 13, fontWeight: 600, border: '1px solid var(--coral)', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>
                        Remove
                      </button>
                    </div>
                  ))
                )}
              </div>
            );
          })
        )}

        {/* Email diagnostics — confirm signup notifications actually deliver. */}
        <div style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 18, marginBottom: 12 }}>Signup email alerts</h2>
          <div className="card">
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 12 }}>
              You should get an email every time someone signs up. Send a test to check delivery — it uses the exact same path as the real alerts.
            </p>
            <button onClick={sendTestEmail} disabled={emailTesting} className="btn-primary" style={{ width: 'auto', padding: '10px 18px', fontSize: 14 }}>
              {emailTesting ? 'Sending…' : 'Send test email'}
            </button>
            {emailResult && (
              <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 10, fontSize: 13, lineHeight: 1.6, background: emailResult.ok ? 'var(--green-light)' : 'var(--coral-light)', color: emailResult.ok ? 'var(--green)' : 'var(--coral)' }}>
                {emailResult.ok ? (
                  <>
                    <p style={{ fontWeight: 700 }}>✅ Sent to {emailResult.to}</p>
                    <p style={{ color: 'var(--text-secondary)' }}>From: {emailResult.from}. Check that inbox now (and the spam / promotions folders). If it's not there in a minute, the sending domain likely isn't verified in Resend.</p>
                  </>
                ) : (
                  <>
                    <p style={{ fontWeight: 700 }}>❌ Could not send</p>
                    <p style={{ marginTop: 4, wordBreak: 'break-word' }}>{emailResult.error}</p>
                    <p style={{ color: 'var(--text-secondary)', marginTop: 6 }}>From: {emailResult.from || '(unknown)'} · API key set: {emailResult.hasKey ? 'yes' : 'no'}</p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Usage metering (Stage 4) — counting only, no cap enforced */}
        <div style={{ marginTop: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
            <h2 style={{ fontSize: 18 }}>Usage this month</h2>
            {usagePeriod && <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{usagePeriod}</span>}
          </div>
          <div className="card">
            {usage.length === 0 ? (
              <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>No usage recorded yet this month.</p>
            ) : (
              usage.map((row) => (
                <div key={row.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f0ece6' }}>
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 600 }}>{row.name || row.email}</p>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{row.email}</p>
                  </div>
                  <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--teal)' }}>{row.workups} <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)' }}>workup{row.workups === 1 ? '' : 's'}</span></span>
                </div>
              ))
            )}
          </div>
        </div>
        </>)}

        {tab === 'leads' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12 }}>
              <h2 style={{ fontSize: 18 }}>School license leads</h2>
              <button onClick={downloadCsv} disabled={leads.length === 0} className="btn-small" style={{ opacity: leads.length === 0 ? 0.5 : 1 }}>Download CSV</button>
            </div>

            {leadsErr && <div style={{ background: 'var(--coral-light)', color: 'var(--coral)', padding: '12px 16px', borderRadius: 8, fontSize: 14, marginBottom: 16 }}>{leadsErr}</div>}

            {leadsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}><div className="spinner" /></div>
            ) : leads.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', padding: '24px 0' }}>No leads yet. Requests from the /schools form will appear here, newest first.</p>
            ) : (
              leads.map(l => (
                <div key={l.id} className="card" style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
                    <div>
                      <p style={{ fontWeight: 700, fontSize: 16 }}>{l.schoolName}</p>
                      <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        {[l.district, l.state, l.studentCount].filter(Boolean).join(' · ') || '-'}
                      </p>
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{fmtDateTime(l.createdAt)}</span>
                  </div>

                  <div style={{ fontSize: 14, marginBottom: 6 }}>
                    <span style={{ fontWeight: 600 }}>{l.contactName}</span>
                    {l.role ? <span style={{ color: 'var(--text-secondary)' }}> · {l.role}</span> : null}
                  </div>
                  <div style={{ fontSize: 14, marginBottom: 6 }}>
                    <a href={`mailto:${l.workEmail}`} style={{ color: 'var(--teal)', fontWeight: 600 }}>{l.workEmail}</a>
                    {l.phone ? <span style={{ color: 'var(--text-secondary)' }}> · {l.phone}</span> : null}
                  </div>
                  {l.situation && (
                    <p style={{ fontSize: 13, color: 'var(--charcoal)', lineHeight: 1.6, whiteSpace: 'pre-wrap', background: 'var(--bg)', borderRadius: 8, padding: '10px 12px', marginTop: 8 }}>{l.situation}</p>
                  )}

                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Status</label>
                    <select value={l.status} onChange={e => changeLeadStatus(l.id, e.target.value)} style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }}>
                      {LEAD_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={() => openQuote(l)} className="btn-small" style={{ marginLeft: 'auto' }}>
                      {quoteFor === l.id ? 'Close' : 'Send quote'}
                    </button>
                  </div>

                  {quoteFor === l.id && (() => {
                    const tier = selectTier(quoteCount || l.studentCount);
                    const over = tier && (tier as any).contactSean;
                    return (
                      <div style={{ marginTop: 10, background: 'var(--bg)', borderRadius: 8, padding: '12px 14px' }}>
                        <p style={{ fontSize: 13, marginBottom: 8 }}><strong>{l.schoolName}</strong> · {l.contactName} · {l.workEmail}</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                          <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Students</label>
                          <select value={quoteCount} onChange={e => setQuoteCount(e.target.value)} style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }}>
                            <option value="">Select a range</option>
                            {STUDENT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        {!tier ? (
                          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Pick a student range to see the tier.</p>
                        ) : over ? (
                          <p style={{ fontSize: 13, color: 'var(--coral)', fontWeight: 600 }}>Over 1,200 students: contact Sean to quote this by hand.</p>
                        ) : (
                          <p style={{ fontSize: 14 }}>
                            Quote: <strong>{(tier as any).label}</strong>, <strong>{formatPrice((tier as any).price)}</strong> per year.
                            Emails {l.workEmail} an accept link and marks this lead quoted.
                          </p>
                        )}
                        {quoteMsg && <p style={{ fontSize: 13, color: 'var(--coral)', marginTop: 8 }}>{quoteMsg}</p>}
                        <button onClick={() => sendQuote(l.id)} className="btn-primary" style={{ marginTop: 10, padding: '10px 14px' }} disabled={quoteBusy || !tier || !!over}>
                          {quoteBusy ? 'Sending…' : 'Send quote'}
                        </button>
                      </div>
                    );
                  })()}
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'outreach' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
              <h2 style={{ fontSize: 18 }}>Outreach contacts</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <select value={outreachFilter} onChange={e => changeOutreachFilter(e.target.value)} style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }}>
                  <option value="">All statuses</option>
                  {OUTREACH_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={downloadOutreachCsv} disabled={outreach.length === 0} className="btn-small" style={{ opacity: outreach.length === 0 ? 0.5 : 1 }}>Download CSV</button>
              </div>
            </div>

            {outreachErr && <div style={{ background: 'var(--coral-light)', color: 'var(--coral)', padding: '12px 16px', borderRadius: 8, fontSize: 14, marginBottom: 16 }}>{outreachErr}</div>}

            {outreachLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}><div className="spinner" /></div>
            ) : outreach.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', padding: '24px 0' }}>
                {outreachFilter ? `No contacts with status "${outreachFilter}".` : 'No outreach contacts yet. They are added through the /api/outreach API.'}
              </p>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, whiteSpace: 'nowrap' }}>
                    <thead>
                      <tr style={{ background: 'var(--teal-light)' }}>
                        {['Email', 'Name', 'Organization', 'State', 'Segment', 'Status', 'Next send'].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: '10px 12px', color: 'var(--teal)', fontWeight: 700 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {outreach.map(c => (
                        <tr key={c.email} style={{ borderTop: '1px solid #f0ece6' }}>
                          <td style={{ padding: '10px 12px' }}><a href={`mailto:${c.email}`} style={{ color: 'var(--teal)', fontWeight: 600 }}>{c.email}</a></td>
                          <td style={{ padding: '10px 12px' }}>{[c.firstName, c.lastName].filter(Boolean).join(' ') || '-'}</td>
                          <td style={{ padding: '10px 12px' }}>{c.organization || '-'}</td>
                          <td style={{ padding: '10px 12px' }}>{c.state || '-'}</td>
                          <td style={{ padding: '10px 12px' }}>{c.segment || '-'}</td>
                          <td style={{ padding: '10px 12px' }}><span className="badge badge-teal" style={{ fontSize: 12 }}>{c.status}</span></td>
                          <td style={{ padding: '10px 12px' }}>{fmtDateTime(c.nextSendAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
